<template>
  <div class="p-4">
    <v-card flat rounded="lg" class="stock-transaction-card">
      <v-card-title class="d-flex align-center justify-space-between px-4 py-3">
        <span class="text-h6 font-weight-medium">Stock Transactions</span>
        <v-btn color="primary" variant="tonal" rounded="lg" @click="dialog = true">
          <font-awesome-icon icon="plus" class="mr-2" />New Transaction
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="grid.headers"
          :items="stockTransactionsPlatformWise"
          :sort-by="[{ key: 'created_at', order: 'desc' }]"
          :items-per-page-options="[10, 15, 20, 30, 50, 100, 200]"
          :items-per-page="10"
          density="comfortable"
          class="custom-data-table"
          item-value="stock_transaction_id"
        >
          <template v-slot:[`item.stock.name`]="{ item }">
            <router-link
              target="_blank"
              name="stock-view"
              :to="{ path: '/stock/view', query: { id: item.stock.code } }"
            >
              {{ item.stock.name }}
            </router-link>
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            <span>{{ $helper.formatPrice(item.amount) }}</span>
          </template>
          <template v-slot:[`item.sub_total`]="{ item }">
            <span>{{ $helper.formatPrice(item.sub_total) }}</span>
          </template>
          <template v-slot:[`item.created_at`]="{ item }">
            <span>{{ $helper.formatDate(item.created_at, true) }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="900px">
      <v-card >
        <v-card-title class="text-h6 font-weight-bold">New Transaction</v-card-title>
        <v-divider></v-divider>

        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="editedItem.stock_transaction_id" label="Transaction ID" disabled />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="editedItem.platform_id"
                  :items="$helper.optionArray(platformList, 'platform_id', 'name')"
                  item-title="label"
                  item-value="code"
                  label="Platform"
                  variant="outlined"
                  hide-details
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="editedItem.stock_id"
                  :items="$helper.optionArray(this.stock, 'stock_id', 'name')"
                  item-title="label"
                  item-value="code"
                  label="Select Stock"
                  variant="outlined"
                  hide-details
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="editedItem.action"
                  :items="$optionHelper.stockAction"
                  item-title="label"
                  item-value="code"
                  label="Action"
                  variant="outlined"
                  hide-details
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="editedItem.qty" label="Quantity" variant="outlined" />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="editedItem.amount" label="Amount" variant="outlined" />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="editedItem.sub_total" label="Sub Total" readonly variant="outlined" />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field v-model="editedItem.stock_avg_price" label="Stock Avg Price" variant="outlined" />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="editedItem.created_at"
                  label="Created At"
                  type="date"
                  variant="outlined"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="close">Cancel</v-btn>
          <v-btn color="primary" variant="tonal" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia';
import { useAlertStore, platformStore } from '@/stores';
import { fetchWrapper } from '@/helpers';
import gql from 'graphql-tag';

const VIEW_STOCK_TRANSACTION_PLATFORM_WISE = gql`
  query StockTransactionsPlatformWise($platformIds: [ID]) {
    stockTransactionsPlatformWise(platform_ids: $platformIds) {
      stock_transaction_id
      platform_id
      stock_id
      action
      qty
      amount
      sub_total
      stock_avg_price
      type
      created_at
      stock {
        stock_id
        name
        code
        market_price
        avg_price
      }
    }
  }
`;

const ADD_STOCK_TRANSACTION = gql`
  mutation AddTransaction(
    $platformId: Int,
    $stockId: Int,
    $action: Int,
    $qty: Int,
    $amount: Float,
    $subTotal: Float,
    $stockAvgPrice: Float,
    $type: Float,
    $createdAt: String
  ) {
    addTransaction(
      platform_id: $platformId,
      stock_id: $stockId,
      action: $action,
      qty: $qty,
      amount: $amount,
      sub_total: $subTotal,
      stock_avg_price: $stockAvgPrice,
      type: $type,
      created_at: $createdAt
    ) {
      stock_transaction_id
      platform_id
      stock_id
      action
      qty
      amount
      sub_total
      stock_avg_price
      type
      created_at
      stock {
        stock_id
        name
        code
        market_price
        avg_price
      }
    }
  }
`;

const PLATFORMS_OPTION_QUERY = gql`
  query Query {
    platforms {
      platform_id
      name
      username
    }
  }
`;

export default {
  name: 'Stock-Transaction',
  data() {
    return {
      dialog: false,
      dialogDelete: false,
      desserts: [],
      editedIndex: -1,
      editedItem: {
        platform_id: '',
        stock_id: '',
        action: '',
        qty: '',
        amount: '',
        sub_total: '',
        stock_avg_price: '',
        type: '',
        created_at: '',
      },
      defaultItem: {
        stock_transaction_id: 0,
      },
      stockTransactionsPlatformWise: [],
      grid: {
        headers: [
          { title: 'Id', key: 'stock_transaction_id' },
          { title: 'Stock', key: 'stock.name' },
          { title: 'Qty', key: 'qty' },
          { title: 'Action', key: 'action' },
          { title: 'Amount', key: 'amount' },
          { title: 'Total Amount', key: 'sub_total' },
          { title: 'Date', key: 'created_at' },
        ],
      },
      stock: [],
      date: new Date().toISOString().substr(0, 10),
      menu: false,
      modal: false,
      menu2: false,
    };
  },
  computed: {
    dateFormatted() {
      return this.input ? new Date(this.input) : '';
    },
    platformWiseStockOptions() {
      const filtered = this.stock.filter(s => s.platform_id === this.editedItem.platform_id);
      return this.$helper.optionArray(filtered, 'stock_id', 'name');
    },
  },
  methods: {
    editItem(item) {
      this.editedIndex = this.desserts.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    async save() {
      await this.AddTransaction();
      await this.close();
    },
    AddTransaction() {
      try {

        this.$apollo.mutate({
          mutation: ADD_STOCK_TRANSACTION,
          variables: {
            platformId: Number(this.editedItem.platform_id),
            stockId: Number(this.editedItem.stock_id),
            action: Number(this.editedItem.action),
            qty: Number(this.editedItem.qty),
            amount: Number(this.editedItem.amount),
            subTotal: Number(this.editedItem.sub_total),
            stockAvgPrice: Number(this.editedItem.stock_avg_price),
            type: 0,
            createdAt: this.editedItem.created_at,
          },
        })
          .then(() => {
            this.alertStore$.success('New transaction add successfully.');
          })
          .catch((error) => {
            this.alertStore$.error(JSON.stringify(error.message));
          });
      } catch (error) {
        this.alertStore$.error(JSON.stringify(error.message));
      }
    },
  },
  setup() {
    const alertStore = platformStore();
    const { platform, platformList } = storeToRefs(alertStore);
    return {
      alertStore$: useAlertStore(),
      platformOptions: platform,
      platformList: platformList,
    };
  },
  watch: {
    'editedItem.amount': function (newVal) {
      this.editedItem.sub_total = newVal * this.editedItem.qty;
    },
    'editedItem.qty': function (newVal) {
      this.editedItem.sub_total = newVal * this.editedItem.amount;
    },
    'editedItem.platform_id': async function (newVal) {
      let canFetchNewStockList = this.stock.filter(
        (stock) => stock.platform_id == this.editedItem.platform_id
      );
      if (canFetchNewStockList.length == 0) {
        await fetchWrapper
          .post('api/stock', { platform_id: newVal })
          .then((response) => {
            this.stock = response.data;
          });
      }
    },
    'editedItem.stock_id': function (newVal) {
      if (this.editedIndex === -1) {
        this.stock.forEach((element) => {
          if (element.stock_id == newVal) {
            this.editedItem.stock_avg_price = element.avg_price;
          }
        });
      }
    },
  },
  apollo: {
    stockTransactionsPlatformWise: {
      query: VIEW_STOCK_TRANSACTION_PLATFORM_WISE,
      variables() {
        return {
          platformIds: JSON.parse(this.platformOptions),
        };
      },
      error(error) {
        this.alertStore$.error(JSON.stringify(error.message));
      },
    },
    platforms: {
      query: PLATFORMS_OPTION_QUERY,
    },
  },
};
</script>

<style scoped>
.stock-transaction-card {
  border: 1px solid var(--border);
}

.custom-data-table :deep(thead th) {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border) !important;
}

.custom-data-table :deep(tbody td) {
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f2f4 !important;
}

.custom-data-table :deep(tbody tr:hover) {
  background-color: #f8f9fb;
}
</style>
